import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import {
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  EmptyComponent,
} from 'nebula-ui-kit';
import {
  applyVaultModuleInitialLoadErrorSignals,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  VaultModuleShellComponent,
  VaultPageAlertComponent,
  type PageHeaderAction,
} from '@nebula-shared/ui';
import { CloudService } from '../../../../data-access/src/lib/cloud/cloud.service';
import { CloudSyncFileStatus } from '../../../../domain/src/lib/cloud/cloud.model';

interface CloudFileRow extends CloudSyncFileStatus {
  projectLabel: string;
  pathLabel: string;
  statusLabel: string;
  sizeLabel: string;
}

@Component({
  selector: 'app-cloud',
  templateUrl: './cloud.component.html',
  styleUrls: ['./cloud.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    ButtonComponent,
    BadgeComponent,
    CheckboxComponent,
    EmptyComponent,
    VaultModuleShellComponent,
    VaultPageAlertComponent,
  ],
})
export class CloudComponent {
  private readonly cloudService = inject(CloudService);
  private readonly destroyRef = inject(DestroyRef);

  private static readonly MODULE_LABEL = 'Respaldo Externo';

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly accessDenied = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorTitle = signal('');
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);

  readonly daemonRunning = signal<boolean | null>(null);
  readonly daemonPid = signal<string>('');
  readonly daemonStats = signal<string>('');
  readonly daemonLogs = signal<string[]>([]);

  readonly rows = signal<CloudFileRow[]>([]);
  readonly selected = signal<Record<string, boolean>>({});
  readonly totalLocal = signal(0);
  readonly totalCloud = signal(0);
  readonly synced = signal(0);
  readonly missing = signal(0);

  readonly headerActions = computed<PageHeaderAction[]>(() => [
    {
      id: 'reload',
      label: 'Actualizar',
      icon: 'RefreshCcw',
      variant: 'secondary',
      loading: this.loading(),
      disabled: this.loading(),
    },
    {
      id: 'upload',
      label: `Subir seleccionados (${this.selectedCount()})`,
      icon: 'Upload',
      variant: 'secondary',
      loading: this.saving(),
      disabled: this.saving() || this.selectedCount() === 0,
    },
  ]);

  readonly selectedCount = computed(() => Object.values(this.selected()).filter(Boolean).length);
  readonly missingRows = computed(() => this.rows().filter((row) => !row.in_cloud));

  constructor() {
    this.reloadAll();
  }

  onHeaderAction(actionId: string): void {
    if (actionId === 'reload') this.reloadAll();
    if (actionId === 'upload') this.uploadSelected();
  }

  toggleSelect(path: string, value: boolean): void {
    this.selected.set({ ...this.selected(), [path]: value });
  }

  selectAllMissing(value: boolean): void {
    const next = { ...this.selected() };
    this.missingRows().forEach((row) => {
      next[row.local_file.relative_path] = value;
    });
    this.selected.set(next);
  }

  startDaemon(): void {
    this.saving.set(true);
    this.cloudService
      .startDaemon()
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.info.set('Daemon iniciado.');
          this.loadDaemonStatus();
        },
        error: (err: unknown) =>
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Respaldo Externo',
              fallback: 'No se pudo iniciar el daemon.',
            }),
          ),
      });
  }

  stopDaemon(): void {
    this.saving.set(true);
    this.cloudService
      .stopDaemon()
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.info.set('Daemon detenido.');
          this.loadDaemonStatus();
        },
        error: (err: unknown) =>
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Respaldo Externo',
              fallback: 'No se pudo detener el daemon.',
            }),
          ),
      });
  }

  loadDaemonStats(): void {
    this.cloudService
      .getDaemonStats()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (stats) => this.daemonStats.set(JSON.stringify(stats, null, 2)),
        error: () => this.daemonStats.set('Sin estadísticas disponibles.'),
      });
  }

  loadDaemonLogs(): void {
    this.cloudService
      .getDaemonLogs(50)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (logs) => this.daemonLogs.set(logs.logs || []),
        error: () => this.daemonLogs.set([]),
      });
  }

  private uploadSelected(): void {
    const selectedPaths = Object.entries(this.selected())
      .filter(([, checked]) => checked)
      .map(([path]) => path);
    if (selectedPaths.length === 0) return;
    this.saving.set(true);
    this.cloudService
      .uploadFiles(selectedPaths)
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.info.set(
            res.message || `Carga finalizada. Subidos: ${res.uploaded || 0}, fallidos: ${res.failed || 0}.`,
          );
          this.selected.set({});
          this.loadSyncStatus();
        },
        error: (err: unknown) =>
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Respaldo Externo',
              fallback: 'No se pudieron subir los archivos.',
            }),
          ),
      });
  }

  private reloadAll(): void {
    this.loadSyncStatus();
    this.loadDaemonStatus();
    this.loadDaemonStats();
    this.loadDaemonLogs();
  }

  private loadSyncStatus(): void {
    this.loading.set(true);
    this.error.set(null);
    this.accessDenied.set(false);
    this.loadError.set(null);
    this.cloudService
      .getSyncStatus()
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.totalLocal.set(res.total_local || 0);
          this.totalCloud.set(res.total_cloud || 0);
          this.synced.set(res.synced || 0);
          this.missing.set(res.missing || 0);
          this.rows.set((res.files || []).map((row) => this.toRow(row)));
        },
        error: (err: unknown) =>
          applyVaultModuleInitialLoadErrorSignals(
            err,
            {
              moduleLabel: CloudComponent.MODULE_LABEL,
              fallback: 'No se pudo cargar el estado cloud.',
            },
            {
              accessDenied: this.accessDenied,
              loadError: this.loadError,
              loadErrorTitle: this.loadErrorTitle,
            },
          ),
      });
  }

  private loadDaemonStatus(): void {
    this.cloudService
      .getDaemonStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.daemonRunning.set(!!res.is_running);
          this.daemonPid.set(String(res.pid || ''));
        },
        error: () => {
          this.daemonRunning.set(null);
          this.daemonPid.set('');
        },
      });
  }

  private toRow(row: CloudSyncFileStatus): CloudFileRow {
    return {
      ...row,
      projectLabel: row.local_file.project_name,
      pathLabel: row.local_file.relative_path,
      statusLabel: row.in_cloud ? 'Sincronizado' : 'Pendiente',
      sizeLabel: this.formatBytes(row.local_file.size),
    };
  }

  private formatBytes(bytes: number): string {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** exp).toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`;
  }
}
